// Offline-aware auth helpers.
//
// Centralizes the decision logging required by the offline-login resilience
// spec, and the "network restored" path that refreshes the Supabase session
// and triggers queue/setting reconciliation. Never calls signOut on a
// network-class refresh failure — that path defers until the network
// returns. Only a confirmed-invalid refresh while online should send the
// user back to /login, and that decision is returned to the caller rather
// than executed here.
//
// OTA-safe: pure JS.

import { supabase } from "@/integrations/supabase/client";
import { clearNeedsLogin, drainPingOutbox } from "@/lib/native/ping-outbox";

export type OfflineAuthDecision =
  | "offline-controls"
  | "offline-login-required"
  | "defer"
  | "login"
  | "refresh-and-sync";

export type OfflineAuthReason =
  | "local session found"
  | "cached trip found"
  | "no cached session"
  | "refresh failed offline"
  | "refresh failed online"
  | "network restored";

export function logOfflineAuth(reason: OfflineAuthReason, decision: OfflineAuthDecision): void {
  // eslint-disable-next-line no-console
  console.log(`[offline-auth] ${reason} decision=${decision}`);
}

function isNetworkClassError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string; status?: number };
  if (typeof e.status === "number" && e.status >= 400 && e.status < 500 && e.status !== 401) {
    // 401/400 invalid_grant is handled below; other 4xx aren't network errors
  }
  const msg = (e.message || "").toLowerCase();
  if (
    e.name === "AuthRetryableFetchError" ||
    e.name === "TypeError" ||
    msg.includes("failed to fetch") ||
    msg.includes("network") ||
    msg.includes("load failed") ||
    msg.includes("timeout") ||
    msg.includes("offline")
  ) {
    return true;
  }
  return false;
}

/**
 * "Session missing" / "no session" — the WebView storage no longer has a
 * Supabase session for us to use or refresh. Distinct from "refresh token
 * invalid" but treated the same way: the user must sign in to recover.
 */
export function isSessionMissingError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: string; code?: string };
  const msg = (e.message || "").toLowerCase();
  if (e.code === "session_not_found" || e.code === "no_session") return true;
  if (msg.includes("auth session missing")) return true;
  return false;
}

function isInvalidRefreshError(err: unknown): boolean {
  if (!err) return false;
  if (isSessionMissingError(err)) return true;
  const e = err as { status?: number; message?: string; code?: string };
  const msg = (e.message || "").toLowerCase();
  if (e.code === "refresh_token_not_found" || e.code === "invalid_grant") return true;
  if ((e.status === 400 || e.status === 401) && /refresh|grant|token/.test(msg)) return true;
  return false;
}

export interface RefreshAndSyncResult {
  shouldLogin: boolean;
}

// --- Sign-out suppression -----------------------------------------------
// When Supabase JS internally calls signOut() after a failed refreshSession,
// it fires a SIGNED_OUT event that would otherwise nuke the in-memory session
// in useAuth and bounce the user to /login mid-session. We don't want that on
// a working app: the user is still authenticated locally (Face ID just
// unlocked them); they just can't reach the backend with a fresh token. Mark
// a short window in which the next SIGNED_OUT should be ignored.

const SUPPRESS_WINDOW_MS = 3000;
let suppressUntil = 0;

export function suppressNextSignOut(): void {
  suppressUntil = Date.now() + SUPPRESS_WINDOW_MS;
}

export function consumeSuppressSignOut(): boolean {
  if (Date.now() < suppressUntil) {
    suppressUntil = 0;
    return true;
  }
  return false;
}

// --- User-initiated sign-out ---------------------------------------------
// A sign-out triggered by the user (Sign Out button, Face ID lock failure)
// MUST propagate. The offline/auto-refresh suppression guards below should
// only swallow SIGNED_OUT events the user didn't ask for. The sign-out call
// sites arm this flag immediately before calling supabase.auth.signOut().

const USER_SIGNOUT_WINDOW_MS = 5000;
let userSignOutUntil = 0;

export function markUserInitiatedSignOut(): void {
  userSignOutUntil = Date.now() + USER_SIGNOUT_WINDOW_MS;
}

export function consumeUserInitiatedSignOut(): boolean {
  if (Date.now() < userSignOutUntil) {
    userSignOutUntil = 0;
    return true;
  }
  return false;
}

export function markNeedsReauthExternal(): void {
  try { sessionStorage.setItem("roava:needs-reauth", "1"); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("roava:needs-reauth")); } catch { /* ignore */ }
}

/** Has refresh failed irrecoverably this session? Read by login gate / UI. */
export function getNeedsReauth(): boolean {
  try {
    return typeof sessionStorage !== "undefined"
      && sessionStorage.getItem("roava:needs-reauth") === "1";
  } catch { return false; }
}

function markNeedsReauth(): void {
  try { sessionStorage.setItem("roava:needs-reauth", "1"); } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent("roava:needs-reauth"));
  } catch { /* ignore */ }
}

/**
 * Try to refresh the Supabase session and, on success, kick the outbox so
 * queued route pings sync. Safe to call whether or not the network is
 * currently available — network-class failures are logged as `defer` and
 * never mutate local state.
 */
export async function refreshSessionAndSync(): Promise<RefreshAndSyncResult> {
  let refreshErr: unknown = null;
  try {
    // Pre-arm the suppression window BEFORE the refresh call: Supabase JS
    // may synchronously fire SIGNED_OUT inside refreshSession() on
    // invalid_grant. Cheap if the refresh succeeds (window expires unused).
    suppressNextSignOut();
    const { error } = await supabase.auth.refreshSession();
    refreshErr = error;
  } catch (err) {
    refreshErr = err;
  }

  if (refreshErr) {
    if (isInvalidRefreshError(refreshErr)) {
      logOfflineAuth("refresh failed online", "login");
      markNeedsReauth();
      return { shouldLogin: true };
    }
    // Treat anything else (network failures, timeouts, fetch errors) as offline.
    logOfflineAuth("refresh failed offline", "defer");
    // Not an auth failure — clear the suppression window so a real future
    // signOut still propagates.
    consumeSuppressSignOut();
    return { shouldLogin: false };
  }

  logOfflineAuth("network restored", "refresh-and-sync");
  consumeSuppressSignOut();

  // A successful refresh means we have a usable session again — clear any
  // outbox needs-login flag so the next drain attempt actually runs.
  try { clearNeedsLogin(); } catch { /* ignore */ }
  // Fire-and-forget; never throws to the auth path.
  void drainPingOutbox().catch(() => {});
  return { shouldLogin: false };
}

/** True when the browser believes it is offline. */
export function isOfflineNow(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}
